import { Table$ } from './Table$';
import { createStore$ } from '../../createStore$';
import without from 'lodash/without';
import sum from 'lodash/sum';
import keyBy from 'lodash/keyBy';
import { getProductPrice } from './util/getProductPrice';
import { XV2 } from '../../XV2';

export namespace Basket$ {
    type State = {
        productsById: Record<number, Product[]>;
        courseProductRefs: Record<string, string[]>;
    };

    const store = createStore$<State>({
        initialState: {
            productsById: {},
            courseProductRefs: {},
        },
    });

    const { useState, useValue } = store;

    export type Product = XV2.API.V1AppPlaceOrder.Input['products'][0];

    export type UseMenuProductAddInput = {
        modGroupIds: Record<number, number[]>;
        variantProductId?: number;
    };

    export function useMenuProduct(product: Table$.Product) {
        const { id } = product;
        const [products, setState] = useState((state) => state.productsById[id] || []);

        return {
            products: products,
            count: products.length,
            add(input: UseMenuProductAddInput) {
                const { modGroupIds, variantProductId } = input;
                const courseId = product.defaultCourseId;
                const ref = Math.random().toString();

                const basketProduct: Product = {
                    ...product,
                    ref: ref,
                    productId: product.id,
                    courseId: courseId,
                    modGroupIds: modGroupIds,
                    variantProductId: variantProductId,
                };

                setState((state) => {
                    state.productsById[product.id] = state.productsById[product.id] || [];
                    state.courseProductRefs[courseId] = state.courseProductRefs[courseId] || [];

                    state.productsById[product.id].push(basketProduct);
                    state.courseProductRefs[courseId].push(ref);
                });
            },
            remove(count = 1) {
                setState((state) => {
                    const targetIndex = state.productsById[product.id].length - Math.abs(count);

                    const productRefsToRemove = state.productsById[product.id]
                        .slice(targetIndex)
                        ?.map?.((item) => item.ref);

                    state.productsById[product.id] = state.productsById[product.id].slice(0, targetIndex);

                    productRefsToRemove.forEach((ref) => {
                        Object.keys(state.courseProductRefs).forEach((courseId) => {
                            if (state.courseProductRefs[courseId].includes(ref)) {
                                state.courseProductRefs[courseId] = without(state.courseProductRefs[courseId], ref);
                            }
                        });
                    });

                    // TODO: HIDE BASKET
                });
            },
            removeByRef(ref: string) {
                setState((state) => {
                    const { productsById, courseProductRefs } = state;

                    Object.keys(productsById).forEach((id) => {
                        const byRef = keyBy(productsById[id], (item) => item.ref);
                        const product = byRef[ref];

                        if (product) {
                            productsById[id] = without(productsById[id], product);
                            courseProductRefs[product.courseId] = without(courseProductRefs[product.courseId], ref);
                        }
                    });
                });
            },
        };
    }

    export function useSummary() {
        const [{ productsById, courseProductRefs }, setState] = useState();
        const products = Object.values(productsById).flat();

        return {
            setState: setState,
            products: products,
            courseProductRefs: courseProductRefs,
            isPendingOrder: products.length > 0,
            productCount: products.length,
            reset() {
                reset();
            },
        };
    }

    export function useTotalCost() {
        const { getProductById } = Table$.useMenu();
        const basketProducts = useValue((state) => Object.values(state.productsById).flat());

        return sum(
            basketProducts.map((item) =>
                getProductPrice({
                    product: getProductById(item.productId),
                    productPrice: (getProductById(item.variantProductId || 0) || getProductById(item.productId)).price,
                    modGroupIds: item.modGroupIds,
                }),
            ),
        );
    }

    export function reset() {
        store.reset();
    }
}
